import {v3, v2, quat} from "oc-threejs/short3.js";
import Maths3 from "oc-threejs/Maths3.js";
import ConnectorHelper from "./ConnectorHelper";
import ConnectorType from "./ConnectorType";
import ListenerMixin from "oc-commons/ListenerMixin";
import Commons from "oc-commons/Commons";
import Maths from 'oc-commons/Maths';
import Scenes from "oc-threejs/Scenes.js";

/**
 * Lauffähiges Beispiel unter:
 * http://preview.objectcode.de/HoloExamples/#oc-interaction.Connectors
 */
export default class Connector extends ListenerMixin(THREE.Object3D) {

    static EPSILON = 0.000001;

    /** @type {ConnectorType} */
    type;
    /** @type {?Connector} */
    connected;
    disabled = false;
    isRemoveOnDrop = false;
    isUnbreakable = false;
    isConnectedWithOwner = false;
    strengthFactor = 1;
    anchorPlane=null;

    /**
     * Creates an instance of Connector.
     * @param {(ConnectorType | Array<string>)} type
     */
    constructor(type) {
        super();
        this.type = ConnectorType.ensureType(type);
        this.connected = null;
        this.helper = new ConnectorHelper(this);
        this.add(this.helper);
        // STAGE.addAsPseudoChild(this, this.helper);
        // if (type.figure == 'Anchor') {
        //     // Anchors sollen die Bounding Box erweitern
        //     this.add(Scenes.createPointObject());
        // }
    }

    getAnchorPlane(){
        if (this.anchorPlane!=null){
            return this.anchorPlane;
        }
        return STAGE.settings.connectors.anchorPlane;
    }

    /**
     * @param {Connector} other
     */
    matches(other) {
        const pair = (figure1, figure2) =>
        (this.type.figure == figure1 && other.type.figure == figure2)
        || (this.type.figure == figure2 && other.type.figure == figure1);
        return this.type.name == other.type.name
            && (pair('Hook', 'Eyelet')
                || pair('Magnet', 'Magnet')
                || pair('Magnet', 'Hook')
                || pair('Magnet', 'Eyelet')
            );
    }

    /**
     * @param {Connector} other
     */
    isCompetitor(other) {
        const pair = (figure1, figure2) =>
        (this.type.figure == figure1 && other.type.figure == figure2)
        || (this.type.figure == figure2 && other.type.figure == figure1);
        return this.type.name == other.type.name
            && (pair('Hook', 'Hook')
                || pair('Eyelet', 'Eyelet')
                || pair('Magnet', 'Magnet')
                || pair('Magnet', 'Hook')
                || pair('Magnet', 'Eyelet')
            );
    }

    isAnchor() {
        return this.type.figure=='Anchor';
    }

    /**
     * @param {Connector} other
     */
    samePositionAndMatches(other) {
        if (!this.matches(other)) {
            return false;
        }
        return this.samePosition(other);
    }

    /**
     * @param {Connector} other
     */
    samePosition(other) {
        const a = this.getWorldPosition(v3());
        const b = other.getWorldPosition(v3());
        return a.distanceToSquared(b) < Connector.EPSILON;
    }

    disconnect() {
        const otherConnector = this.connected;
        if (!otherConnector) {
            // allready disconnected
            return;
        }
        this.trigger('disconnect', this, otherConnector);
        otherConnector.trigger('disconnect', otherConnector, this);
        otherConnector.connected = null;
        this.connected = null;
    }

    /**
     * @param {Connector} otherConnector
     */
    connect(otherConnector) {
        if (otherConnector === this.connected && otherConnector != null && otherConnector.connected === this) {
            // allready connected
            return;
        }
        if (this.connected) {
            this.disconnect();
        }
        if (otherConnector.connected) {
            otherConnector.disconnect();
        }
        this.connected = otherConnector;
        otherConnector.connected = this;
        this.trigger('connect', this, otherConnector);
        otherConnector.trigger('connect', otherConnector, this);
    }

    /**
     * @param {Connector} otherConnector
     * @param {number} [slerpFactor=1]
     */
    moveTo(otherConnector, slerpFactor = 1) {
        const owner = Scenes.getLastUncommenAnchestor(this.parent,otherConnector);
        if (owner==null) {
            throw new Error('dragged object is not in scene');
        }

        const parents= Array.from(Scenes.getSelfAndAncestorSet(this));
        const ownerIndex= parents.indexOf(owner);
        parents.splice(ownerIndex+1,parents.length);
        Scenes.bring({
            what: parents.reverse(),
            to: [otherConnector,Maths3.quaternion.y180],
            rotate: true,
            translate: true,
            slerpFactor
        });
    }

    isFreeForDrop() {
        return !this.disabled && !this.isConnectedWithOwner && (this.isRemoveOnDrop || Connector.isParentDropExchange(this) || this.connected == null || (this.connected && Connector.isParentDropExchange(this.connected)));
    }

    getDropPrio() {
        return this.isFreeForDrop() ? 0 : 1;
    }

    /**
     * @param {Connector} connectorOfOwner
     */
    connectWithOwner(connectorOfOwner) {
        if (!this.matches(connectorOfOwner)) {
            this.matches(connectorOfOwner);
            throw "connectWithOwner doesn't match: " + this + " and " + connectorOfOwner;
        }
        this.isConnectedWithOwner = true;
        this.isUnbreakable = true;
        this.moveTo(connectorOfOwner);
        this.parent.owner = connectorOfOwner.parent;
        this.connect(connectorOfOwner);
        this.on('disconnect', () => {
            Scenes.remove(this.parent);
            this.isConnectedWithOwner = false;
        });
        return {
            andRemoveOnDrop: () => {
                this.parent.isRemoveOnDrop = true;
            },
        }
    }

    toString() {
        return this.parent + '/[' + this.type.name + ',' + this.type.figure + '](' + this.id + ')' + (this.connected ? '+' : '-');
    }

    static allConnectors() {
        const l = [];
        STAGE.scene.traverse(o => {
            if (Connector.isConnectorNotAnchor(o)) {
                l.push(o);
            }
        });
        return l;
    }

    static updateConnectedConnectors() {
        //console.log('updateConnectedConnectors');

        const t0 = Date.now();

        let iteration = 0;

        Commons.iterateWhileDirty(() => {

            Commons.iterateWhileDirty(() => {

                if (iteration++ > 100) {
                    throw 'updateConnectedConnectors exceeds iteration limit';
                }

                let dirty = false;
                const connectors = Connector.allConnectors();
                connectors.forEach(c => {
                    if (c.connected && (!c.samePositionAndMatches(c.connected) || Scenes.isOrphan(c.connected))) {
                        c.disconnect();
                        // console.log('(1) disconnect '+c);
                        dirty = true;
                    }
                });

                reconnect: for (let i = 0; i < connectors.length; i++) {
                    const a = connectors[i];
                    for (let j = i + 1; j < connectors.length; j++) {
                        const b = connectors[j];
                        if (a.connected != b && a.samePositionAndMatches(b) && a.getDropPrio() <= b.getDropPrio()
                            && !((a.connected && a.connected.parent===b.parent) || (b.connected && b.connected.parent===a.parent))) {
                            if (a.connected) {
                                // console.log(b.getDropPrio());
                                // b.parent.isRemoveOnDrop ist true!

                                // const log = (s,x) => {
                                //    console.log(s+x+', dropPrio: '+x.getDropPrio()+', isRemoveOnDrop: '+x.isRemoveOnDrop+', parent.isRemoveOnDrop: '+x.parent.isRemoveOnDrop);
                                // }
                                // const logall = (x) => {
                                //    if (x) {
                                //        log('',x);
                                //        if (x.connected) {
                                //            log('  +--> ',x.connected);
                                //            if (x.connected.connected) {
                                //                log('         +--> ', x.connected.connected);
                                //            }
                                //        }
                                //    }
                                // }
                                // logall(a);
                                // logall(b);

                                const old = a.connected;
                                a.disconnect();
                                //console.log('(2) disconnect '+a);
                                Scenes.remove(old.parent);
                                dirty = true;
                                break reconnect;
                            }
                            a.connect(b);
                            // console.log('(3) connect '+a+' '+b);
                            dirty = true;
                        }
                    }
                }

                return dirty;
            });

            const onUpdateConnectedConnectorsList = Scenes.filter(STAGE.scene, o => o.onUpdateConnectedConnectors);
            const connector = onUpdateConnectedConnectorsList.find(o => o.onUpdateConnectedConnectors());
            //if (connector) {
            //    console.log('(4) dirty '+connector);
            //}
            return connector;
        });

        //console.log('Connector.updateConnectedConnectors took ', Date.now()-t0, 'ms and ',iteration,' iterations' );
        STAGE.trigger('updatedConnectedConnectors');
        APP.trigger('updatedConnectedConnectors');
    }

    /**
     * @param {THREE.Object3D} object
     * @param {boolean} dropped
     * @param {boolean} [applyForces=true]
     */
    static applyForces(object, dropped, applyForces = true) {

        const connectorSettings = STAGE.settings.connectors;

        const forces = object.getConnectorForces();

        if (applyForces && !Commons.isEmpty(forces)) {
            const dominating = forces[0];

            if (dropped) {

                if (dominating.strength > 1 - Connector.EPSILON
                    || dominating.distance < connectorSettings.snapDistance
                ) {
                    dominating.own.moveTo(dominating.other);
                }

            } else {

                // Anziehung / Ausrichtung
                if (object.rotateIfDragged) {
                    const originPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(connectorSettings.anchorPlane.normal, v3(0, 0, 0));
                    const steps = connectorSettings.forceShuffleSteps;
                    const qf = quat();
                    for (var i = 0; i < steps; i++) {
                        (steps > 1 ? _.shuffle(_.clone(forces)) : forces).forEach(force => {
                            const xo = object.getWorldPosition(v3());
                            const xa = force.own.getWorldPosition(v3());
                            const xb = force.other.getWorldPosition(v3());
                            const inverseObjectQuaternion = object.quaternion.clone().invert();
                            const planeVector = (v) => originPlane.projectPoint(v,v3());
                            const dxo = (v) => v.clone().sub(xo);
                            const unitVector = (v) =>
                                planeVector(dxo(v)).normalize().applyQuaternion(inverseObjectQuaternion);
                            const q = new THREE.Quaternion().setFromUnitVectors(unitVector(xa), unitVector(xb));
                            qf.slerp(q, force.strength);
                        });

                    }
                    object.quaternion.multiply(qf);
                    object.updateMatrixWorld();
                }

                // snap während Drag
                if (dominating.distance < connectorSettings.snapDistance) {
                    const av = dominating.own.getWorldPosition(v3());
                    const bv = dominating.other.getWorldPosition(v3());
                    const dv = bv.clone().sub(av);
                    object.position.add(dv);
                    object.updateMatrixWorld();
                }
            }
        }

        const connectors = Connector.allConnectors();
        connectors.forEach(x => x.pulseStrength = 0);
        forces.forEach(force => {
            force.own.pulseStrength = Math.max(force.pulseStrength, force.own.pulseStrength);
            force.other.pulseStrength = Math.max(force.pulseStrength, force.other.pulseStrength);
        });
    }

    static isParentDropExchange(c) {
        return c.parent.isRemoveOnDrop || c.parent.isDropExchange;
    }

    static isConnectorNotAnchor(x) {
        return x instanceof Connector && x.type.figure != 'Anchor';
    }

    static getConnectorForce(distance, distanceWhereForceIs0, distanceWhereForceIs1) {
        const strength = Connector.getConnectorForceUnbounded(distance, distanceWhereForceIs0, distanceWhereForceIs1);
        return Math.min(strength, 1);
    }

    static getConnectorForceUnbounded(distance, distanceWhereForceIs0, distanceWhereForceIs1, forceFactor = 1, connectorAngle = 0, angle0 = -0.0001, openingAngleForce = 1, angle1 = Math.PI+0.0001) {

        if (distance < Connector.EPSILON) return Number.MAX_VALUE;
        if (distance > distanceWhereForceIs0) return 0;

        const angleForce = Maths.mapToUnitInterval(angle0,connectorAngle,angle1) * (1-openingAngleForce) + openingAngleForce;

        let normalizedDistance = (distanceWhereForceIs0 - distance) / (distanceWhereForceIs0 - distanceWhereForceIs1);

        let force = Math.pow(normalizedDistance, 2) * forceFactor * angleForce;

        return force;

    }

    // static plotForceField(angleDegree) {
    //     const size = 512;
    //     const img = Images.plotCanvas(size,size);
    //     const zoom = 8;
    //     const xy = (x,y) => v3(x/size-0.5,0,y/size-0.5).multiplyScalar(zoom);
    //     const center = v3();
    //
    //     const connectorAngle = angleDegree * Math.PI/180;
    //
    //     const connectorSettings = () => STAGE.settings.connectors;
    //
    //     const openingAngleForce = connectorSettings().openingAngleForce;
    //     const angle0 = Maths.cutBetween(0,360-connectorSettings().openingAngle,360)*Math.PI/360-0.0001;
    //     const angle1 = Maths.cutBetween(0,360-connectorSettings().openingAngleWhereForceIs1,360)*Math.PI/360+0.0001;
    //     const forceFactor = 1;
    //     const fq0 = connectorSettings().distanceWhereForceIs0;
    //     const fq1 = connectorSettings().distanceWhereForceIs1;
    //
    //     // for (let x=0;x<size;x++) {
    //     //     const connectorAngle = (x/size)*Math.PI;
    //     //     const angleForce = Maths.mapToUnitInterval(angle0,connectorAngle,angle1) * (1-openingAngleForce) + openingAngleForce;
    //     //     const y = angleForce * size;
    //     //     img.grayDot(x*0.8+0.1*size,(size-y)*0.8+0.1*size,1);
    //     // }
    //
    //     img.forRaster((x,y)=>{
    //         const p = xy(x,y);
    //         const distance = p.distanceTo(center);
    //         const connectorAngle = p.clone().sub(center).angleTo(v3(0,0,1));
    //
    //         let force = Connector.getConnectorForceUnbounded(distance, fq0, fq1, forceFactor, connectorAngle, angle0, openingAngleForce, angle1);
    //         force = Math.min(force, 1);
    //         img.grayDot(x,y,force);
    //
    //         // img.grayDot(x,y,connectorAngle/Math.PI);
    //
    //         if (Math.abs(distance-fq1)<0.02) {
    //             img.rgbaDot(x,y,255,0,0,255);
    //         }
    //         if (Math.abs(distance-fq0)<0.02) {
    //             img.rgbaDot(x,y,0,0,255,255);
    //         }
    //     });
    //
    //     img.show();
    // }

    setConnectorOptions(options) {
        if (options.isRemoveOnDrop) {
            this.isRemoveOnDrop = true;
        }
        if (options.isUnbreakable) {
            this.isUnbreakable = true;
        }
        if (options.position) {
            this.position.copy(options.position);
        }
        if (options.quaternion) {
            this.quaternion.copy(options.quaternion);
        }
        if (options.userData) {
            this.userData = Commons.cloneDeep(options.userData);
        }
        if(options.anchorPlane){
            const plane={
                normal: options.anchorPlane.normal ? options.anchorPlane.normal : v3(0, 1, 0),
                constant: options.anchorPlane.constant ? options.anchorPlane.constant : 0
            };
            this.anchorPlane=new THREE.Plane(plane.normal.clone(),plane.constant);
        }
    }

    static connectorOptions=["isRemoveOnDrop","isUnbreakable","position","quaternion","userData","anchorPlane"];


    getBoundingBox() {
        return new THREE.Box3(v3(0, 0, 0), v3(0, 0, 0));
    }

    /**
     * positioniert den Connector um den Parent herum, und zwar bezogen auf die BoundingBox.
     * Dabei wird der Connector so orientiert, dass er vom Zentrum der BoundingBox weg zeigt.
     * 0,0,0 ist min und 1,1,1 max der BoundingBox
     * @param gridPosition
     */
    aroundParent(gridPosition,orientationOrthogonalToFace=false) {
        this.posbox.center = this.parent.posbox.grid(gridPosition.x,gridPosition.y,gridPosition.z);
        let centerOfParentDirection = this.parent.posbox.center.clone().sub(this.posbox.center).normalize();
        if (orientationOrthogonalToFace) {
            centerOfParentDirection = Maths3.nearestBoxFaceNormal(centerOfParentDirection);
        }
        // if (this.isAnchor()) {
        //     centerOfParentDirection.negate();
        // }
        this.quaternion.setFromUnitVectors ( Maths3.vector.y, centerOfParentDirection );
        return this;
    }

}

